import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Eye, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import api, { formatApiError } from "@/lib/api";
import { ARTICLE_CATEGORIES } from "@/lib/constants";

const STATUS_OPTIONS = {
  articles: ["draft", "scheduled", "published", "archived"],
  testimonials: ["draft", "approved", "published", "archived"],
  services: ["draft", "published", "hidden"],
  faqs: ["draft", "published"],
  team: ["draft", "published", "hidden"],
};

// field types: text, textarea, richtext, number, category, lines, faqs, considerations, bool
const SCHEMAS = {
  articles: {
    label: "Article", titleKey: "title", preview: (i) => `/insights/${i.slug}`,
    fields: [
      { key: "title", label: "Title", type: "text" },
      { key: "slug", label: "Slug (URL)", type: "text" },
      { key: "category", label: "Category", type: "category" },
      { key: "excerpt", label: "Excerpt", type: "textarea" },
      { key: "image", label: "Hero image URL", type: "text" },
      { key: "read_minutes", label: "Read minutes", type: "number" },
      { key: "author", label: "Author", type: "text" },
      { key: "body", label: "Article body (HTML)", type: "richtext" },
      { key: "related_service", label: "Related service slug", type: "text" },
      { key: "published_at", label: "Published date (ISO)", type: "text" },
      { key: "updated_at", label: "Last reviewed date (ISO)", type: "text" },
      { key: "seo_title", label: "SEO title", type: "text" },
      { key: "seo_desc", label: "SEO description", type: "textarea" },
      { key: "disclaimer", label: "Disclaimer", type: "textarea" },
    ],
  },
  testimonials: {
    label: "Testimonial", titleKey: "display_name", preview: () => `/testimonials`,
    note: "Only publish genuine, consented client feedback. Never attach an AI-generated face as a real client.",
    fields: [
      { key: "quote", label: "Testimonial quote", type: "textarea" },
      { key: "display_name", label: "Display name / initials", type: "text" },
      { key: "service_category", label: "Service category", type: "text" },
      { key: "date", label: "Date (optional)", type: "text" },
      { key: "photo_url", label: "Client photo URL (optional, with consent only)", type: "text" },
      { key: "consent_recorded", label: "Consent recorded", type: "bool" },
    ],
  },
  services: {
    label: "Service", titleKey: "title", preview: (i) => `/services/${i.slug}`,
    note: "You can edit copy, FAQs and CTA. The page layout and branding are locked so routine editing cannot break the design.",
    fields: [
      { key: "title", label: "Title", type: "text" },
      { key: "slug", label: "Slug (URL)", type: "text" },
      { key: "category", label: "Category", type: "text" },
      { key: "short_desc", label: "Short description", type: "textarea" },
      { key: "hero_intro", label: "Hero intro", type: "textarea" },
      { key: "who_for", label: "Who this may suit", type: "textarea" },
      { key: "image", label: "Card image URL", type: "text" },
      { key: "order", label: "Display order", type: "number" },
      { key: "how_we_help", label: "How ApexPath helps (one per line)", type: "lines" },
      { key: "considerations", label: "Considerations (label :: text, one per line)", type: "considerations" },
      { key: "faqs", label: "FAQs (question :: answer, one per line)", type: "faqs" },
      { key: "cta_label", label: "CTA label", type: "text" },
      { key: "seo_title", label: "SEO title", type: "text" },
      { key: "seo_desc", label: "SEO description", type: "textarea" },
    ],
  },
  faqs: {
    label: "FAQ", titleKey: "question", preview: null,
    fields: [
      { key: "question", label: "Question", type: "text" },
      { key: "answer", label: "Answer", type: "textarea" },
      { key: "category", label: "Category", type: "text" },
      { key: "order", label: "Display order", type: "number" },
      { key: "services", label: "Assign to service slugs (one per line)", type: "lines" },
    ],
  },
  team: {
    label: "Team member", titleKey: "name", preview: () => `/about`,
    note: "Only publish titles that reflect current formal authorisation.",
    fields: [
      { key: "name", label: "Name", type: "text" },
      { key: "title", label: "Role / title", type: "text" },
      { key: "bio", label: "Biography", type: "textarea" },
      { key: "image_url", label: "Profile photo URL", type: "text" },
      { key: "order", label: "Display order", type: "number" },
    ],
  },
};

function toForm(schema, item) {
  const f = {};
  schema.fields.forEach((fld) => {
    let v = item?.[fld.key];
    if (fld.type === "lines") v = Array.isArray(v) ? v.join("\n") : "";
    else if (fld.type === "faqs") v = Array.isArray(v) ? v.map((x) => `${x.q} :: ${x.a}`).join("\n") : "";
    else if (fld.type === "considerations") v = Array.isArray(v) ? v.map((x) => `${x.label} :: ${x.text}`).join("\n") : "";
    else if (fld.type === "bool") v = !!v;
    else if (v == null) v = "";
    f[fld.key] = v;
  });
  return f;
}

function fromForm(schema, form) {
  const out = {};
  schema.fields.forEach((fld) => {
    let v = form[fld.key];
    if (fld.type === "lines") v = String(v || "").split("\n").map((x) => x.trim()).filter(Boolean);
    else if (fld.type === "number") v = v === "" ? 0 : Number(v);
    else if (fld.type === "faqs") v = String(v || "").split("\n").map((l) => l.split("::")).filter((p) => p[0]?.trim()).map((p) => ({ q: p[0].trim(), a: (p[1] || "").trim() }));
    else if (fld.type === "considerations") v = String(v || "").split("\n").map((l) => l.split("::")).filter((p) => p[0]?.trim()).map((p) => ({ label: p[0].trim(), text: (p[1] || "").trim() }));
    else if (fld.type === "bool") v = !!v;
    out[fld.key] = v;
  });
  return out;
}

const badgeColor = (s) => ({
  published: "bg-emerald-100 text-emerald-700", approved: "bg-emerald-100 text-emerald-700",
  draft: "bg-amber-100 text-amber-700", scheduled: "bg-blue-100 text-blue-700",
  archived: "bg-slate-200 text-slate-600", hidden: "bg-slate-200 text-slate-600",
}[s] || "bg-slate-100 text-slate-600");

export default function ContentManager({ type }) {
  const schema = SCHEMAS[type];
  const statuses = STATUS_OPTIONS[type];
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // item or {} for new
  const [form, setForm] = useState({});
  const [status, setStatus] = useState("draft");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/content/${type}`).then((r) => setItems(r.data)).catch(() => {});
  }, [type]);
  useEffect(() => { load(); }, [load]);

  const openEdit = (item) => {
    setEditing(item || {});
    setForm(toForm(schema, item));
    setStatus(item?.status || statuses[0]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...fromForm(schema, form), status };
      if (editing?.id) await api.put(`/admin/content/${type}/${editing.id}`, payload);
      else await api.post(`/admin/content/${type}`, payload);
      toast.success(`${schema.label} saved`);
      setEditing(null); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const quickStatus = async (item, newStatus) => {
    try {
      await api.patch(`/admin/content/${type}/${item.id}/status`, { status: newStatus });
      load();
    } catch { toast.error("Could not update status"); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete this ${schema.label.toLowerCase()}?`)) return;
    try { await api.delete(`/admin/content/${type}/${item.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl font-bold text-navy">{schema.label}s</h1>
          <p className="mt-1 text-sm text-slate-500">{items.length} item(s)</p>
        </div>
        <button onClick={() => openEdit(null)} data-testid="content-new-btn" className="btn-gold text-sm"><Plus size={16} /> Add New</button>
      </div>

      {schema.note && <p className="mb-4 rounded-lg border-l-4 border-gold bg-gold/5 p-3 text-xs text-slate-600">{schema.note}</p>}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3">{schema.label}</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((item) => (
              <tr key={item.id} data-testid={`content-row-${item.id}`} className="hover:bg-slate-50">
                <td className="px-5 py-3">
                  <div className="font-medium text-navy line-clamp-1">{item[schema.titleKey] || "(untitled)"}</div>
                  {item.category && <div className="text-xs text-slate-400">{item.category}</div>}
                </td>
                <td className="px-5 py-3">
                  <select value={item.status} onChange={(e) => quickStatus(item, e.target.value)}
                    data-testid={`content-status-${item.id}`}
                    className={`rounded-full px-2.5 py-1 text-xs font-medium ${badgeColor(item.status)}`}>
                    {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center justify-end gap-2">
                    {schema.preview && (
                      <a href={schema.preview(item)} target="_blank" rel="noreferrer" title="Preview" className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-navy"><Eye size={16} /></a>
                    )}
                    <button onClick={() => openEdit(item)} data-testid={`content-edit-${item.id}`} className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-navy"><Pencil size={16} /></button>
                    <button onClick={() => remove(item)} className="rounded-md p-2 text-slate-500 hover:bg-red-50 hover:text-red-600"><Trash2 size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={3} className="px-5 py-10 text-center text-slate-400">No items yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Edit drawer */}
      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setEditing(null)} />
          <div className="relative flex h-full w-full max-w-2xl flex-col bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <h2 className="font-serif text-xl font-semibold text-navy">{editing.id ? "Edit" : "New"} {schema.label}</h2>
              <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-navy"><X size={20} /></button>
            </div>
            <div className="flex-1 space-y-4 overflow-y-auto p-6">
              <div>
                <Label>Status</Label>
                <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="edit-status-select"
                  className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-sm">
                  {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              {schema.fields.map((fld) => (
                <div key={fld.key}>
                  <Label>{fld.label}</Label>
                  {fld.type === "textarea" || fld.type === "lines" || fld.type === "faqs" || fld.type === "considerations" ? (
                    <Textarea value={form[fld.key] || ""} onChange={(e) => setForm((f) => ({ ...f, [fld.key]: e.target.value }))}
                      data-testid={`edit-field-${fld.key}`} className="mt-1.5" rows={fld.type === "textarea" ? 3 : 4} />
                  ) : fld.type === "richtext" ? (
                    <Textarea value={form[fld.key] || ""} onChange={(e) => setForm((f) => ({ ...f, [fld.key]: e.target.value }))}
                      data-testid={`edit-field-${fld.key}`} className="mt-1.5 font-mono text-xs" rows={10} />
                  ) : fld.type === "bool" ? (
                    <div className="mt-2"><Checkbox checked={!!form[fld.key]} onCheckedChange={(v) => setForm((f) => ({ ...f, [fld.key]: !!v }))} data-testid={`edit-field-${fld.key}`} /></div>
                  ) : fld.type === "category" ? (
                    <select value={form[fld.key] || ""} onChange={(e) => setForm((f) => ({ ...f, [fld.key]: e.target.value }))}
                      data-testid={`edit-field-${fld.key}`} className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-sm">
                      <option value="">Select…</option>
                      {ARTICLE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  ) : (
                    <Input type={fld.type === "number" ? "number" : "text"} value={form[fld.key] ?? ""}
                      onChange={(e) => setForm((f) => ({ ...f, [fld.key]: e.target.value }))}
                      data-testid={`edit-field-${fld.key}`} className="mt-1.5" />
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
              <button onClick={() => setEditing(null)} className="text-sm font-medium text-slate-500 hover:text-navy">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="edit-save-btn" className="btn-gold text-sm disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
